import { useMemo, useState, useEffect } from 'react';
import type { MockInspection, MockNCR } from '@/mocks/qms';
import { qcService } from '@/services/qcService';

interface SupplierStats {
  supplierId: string;
  supplierName: string;
  totalInspections: number;
  passed: number;
  failed: number;
  ncrCount: number;
  passRate: number;
  score: number;
  grade: string;
  gradeColor: string;
}

interface Props {
  inspections?: MockInspection[];
  ncrs?: MockNCR[];
}

function getGrade(score: number): { grade: string; color: string } {
  if (score >= 90) return { grade: 'A', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  if (score >= 75) return { grade: 'B', color: 'bg-sky-100 text-sky-700 border-sky-200' };
  if (score >= 60) return { grade: 'C', color: 'bg-amber-100 text-amber-700 border-amber-200' };
  return { grade: 'F', color: 'bg-red-100 text-red-700 border-red-200' };
}

export default function SupplierQualityTab({ inspections: propInspections, ncrs: propNcrs }: Props) {
  const [inspections, setInspections] = useState<MockInspection[]>(propInspections || []);
  const [ncrs, setNcrs] = useState<MockNCR[]>(propNcrs || []);
  const [loading, setLoading] = useState(!propInspections);

  useEffect(() => {
    if (propInspections) {
      setInspections(propInspections);
    }
    if (propNcrs) {
      setNcrs(propNcrs);
    }

    if (!propInspections || !propNcrs) {
      let active = true;
      async function fetchData() {
        try {
          setLoading(true);
          const [inspData, ncrData] = await Promise.all([
            qcService.getInspections({ type: 'INCOMING' }),
            qcService.getNCRs(),
          ]);
          if (active) {
            if (!propInspections) setInspections(inspData || []);
            if (!propNcrs) setNcrs(ncrData || []);
          }
        } catch (err) {
          console.error('Error fetching supplier quality data:', err);
        } finally {
          if (active) setLoading(false);
        }
      }
      fetchData();
      return () => {
        active = false;
      };
    }
  }, [propInspections, propNcrs]);

  const supplierStats = useMemo<SupplierStats[]>(() => {
    const map = new Map<string, SupplierStats>();

    inspections
      .filter((i) => i.type === 'INCOMING')
      .forEach((insp) => {
        const supplierId = insp.supplierId || insp.supplierName || 'unknown-supplier';
        const supplierName = insp.supplierName || 'Direct Vendor';
        if (!supplierName) return;

        if (!map.has(supplierId)) {
          map.set(supplierId, {
            supplierId,
            supplierName,
            totalInspections: 0,
            passed: 0,
            failed: 0,
            ncrCount: 0,
            passRate: 0,
            score: 0,
            grade: '',
            gradeColor: '',
          });
        }
        const s = map.get(supplierId)!;
        s.totalInspections++;
        if (insp.status === 'PASSED') s.passed++;
        if (insp.status === 'FAILED') s.failed++;
      });

    // Count NCRs per supplier
    ncrs.forEach((ncr) => {
      const insp = inspections.find((i) => i.id === ncr.inspectionId);
      if (!insp || insp.type !== 'INCOMING') return;
      const supplierId = insp.supplierId || insp.supplierName || 'unknown-supplier';
      const s = map.get(supplierId);
      if (s) s.ncrCount++;
    });

    // Calculate scores
    const result: SupplierStats[] = [];
    map.forEach((s) => {
      const passRate = s.totalInspections > 0 ? s.passed / s.totalInspections : 0;
      const ncrRate = s.totalInspections > 0 ? s.ncrCount / s.totalInspections : 0;
      const score = (passRate * 0.7 + (1 - ncrRate) * 0.3) * 100;
      const g = getGrade(score);
      result.push({
        ...s,
        passRate: Math.round(passRate * 100),
        score: Math.round(score),
        grade: g.grade,
        gradeColor: g.color,
      });
    });

    return result.sort((a, b) => b.score - a.score);
  }, [inspections, ncrs]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
        <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
        Loading supplier quality metrics...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {supplierStats.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <i className="ri-user-search-line text-4xl text-gray-300 mb-3 block" />
          <p className="text-gray-600 font-medium">No incoming inspections recorded yet</p>
          <p className="text-xs text-gray-400 mt-1">Complete GRN quality inspections to see supplier performance scores</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Supplier</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Total Insp</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Pass</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Fail</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Pass Rate</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">NCRs</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Score</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Grade</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {supplierStats.map((s) => (
                  <tr key={s.supplierId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{s.supplierName}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{s.totalInspections}</td>
                    <td className="px-4 py-3 text-center text-emerald-600 font-medium">{s.passed}</td>
                    <td className="px-4 py-3 text-center text-red-600 font-medium">{s.failed}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-emerald-500"
                            style={{ width: `${s.passRate}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-700 w-8">{s.passRate}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {s.ncrCount > 0 ? (
                        <span className="text-red-600 font-medium">{s.ncrCount}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-gray-900">{s.score}</td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold border ${s.gradeColor}`}
                      >
                        {s.grade}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}